import React, { useState, useRef, useEffect } from 'react'
import { useTranslation } from '@/shared/contexts/TranslationContext'
import { SavedFilter } from '@/shared/components/entities/AdvancedFilters'

interface SavedFiltersManagerProps {
  savedFilters: SavedFilter[]
  onLoad: (filterId: string) => Promise<void>
  onDelete?: (filterId: string) => Promise<void>
  loading?: boolean
}

export function SavedFiltersManager({
  savedFilters,
  onLoad,
  onDelete,
  loading = false
}: SavedFiltersManagerProps) {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleLoad = async (filterId: string) => {
    await onLoad(filterId)
    setIsOpen(false)
  }

  const handleDelete = async (e: React.MouseEvent, filterId: string) => {
    e.stopPropagation()
    if (!onDelete) return

    const confirmKey = 'list.quickFilters.savedFilters.confirmDelete'
    const confirmText = t(confirmKey)
    const confirmMessage = confirmText && confirmText !== confirmKey ? confirmText : 'Deseja realmente excluir este filtro salvo?'

    if (confirm(confirmMessage)) {
      setDeletingId(filterId)
      try {
        await onDelete(filterId)
      } finally {
        setDeletingId(null)
      }
    }
  }

  if (savedFilters.length === 0) return null

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
        title={t('list.quickFilters.savedFiltersLabel')}
      >
        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
        </svg>
        <span className="hidden sm:inline">{t('list.quickFilters.savedFiltersLabel')}</span>
        <span className="px-1.5 py-0.5 bg-slate-200 dark:bg-slate-700 text-xs rounded-full">
          {savedFilters.length}
        </span>
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-72 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg z-50">
          <div className="p-3 border-b border-slate-200 dark:border-slate-700">
            <h3 className="text-sm font-semibold text-slate-900 dark:text-white">
              {t('list.quickFilters.savedFiltersLabel')}
            </h3>
          </div>

          <div className="max-h-96 overflow-y-auto">
            {savedFilters.length === 0 ? (
              <div className="p-8 text-center">
                <svg className="h-12 w-12 text-slate-300 dark:text-slate-600 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                </svg>
                <p className="text-sm text-slate-500 dark:text-slate-400 mb-1">
                  {t('list.quickFilters.savedFilters.emptyTitle')}
                </p>
                <p className="text-xs text-slate-400 dark:text-slate-500">
                  {t('list.quickFilters.savedFilters.emptyDescription')}
                </p>
              </div>
            ) : (
              <div className="py-1">
                {savedFilters.map((filter) => (
                  <div
                    key={filter.id}
                    className="group relative px-3 py-2 hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors"
                  >
                    <button
                      onClick={() => handleLoad(filter.id)}
                      disabled={loading || deletingId === filter.id}
                      className="w-full text-left"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-medium text-slate-900 dark:text-white truncate">
                              {filter.name}
                            </p>
                            {filter.is_default && (
                              <span className="px-1.5 py-0.5 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 text-xs rounded">
                                {t('list.quickFilters.savedFilters.defaultLabel')}
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                            {new Date(filter.created_at).toLocaleDateString('pt-BR', {
                              day: '2-digit',
                              month: 'short',
                              year: 'numeric'
                            })}
                          </p>
                          
                          {/* Preview dos filtros */}
                          {Object.keys(filter.filters).length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-1.5">
                              {Object.entries(filter.filters)
                                .filter(([_, value]) => {
                                  if (value === null || value === undefined || value === '') return false
                                  if (typeof value === 'object' && !Array.isArray(value)) {
                                    return Object.values(value).some(v => v !== null && v !== undefined && v !== '')
                                  }
                                  return true
                                })
                                .slice(0, 3)
                                .map(([key, value]) => (
                                  <span
                                    key={key}
                                    className="px-1.5 py-0.5 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 text-xs rounded"
                                  >
                                    {key}
                                  </span>
                                ))}
                              {Object.keys(filter.filters).length > 3 && (
                                <span className="px-1.5 py-0.5 text-slate-500 dark:text-slate-500 text-xs">
                                  +{Object.keys(filter.filters).length - 3}
                                </span>
                              )}
                            </div>
                          )}
                        </div>

                        {onDelete && !filter.is_default && (
                          <button
                            onClick={(e) => handleDelete(e, filter.id)}
                            disabled={deletingId === filter.id}
                            className="opacity-0 group-hover:opacity-100 p-1 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-all"
                              title={t('actions.delete')}
                          >
                            {deletingId === filter.id ? (
                              <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                              </svg>
                            ) : (
                                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                            )}
                          </button>
                        )}
                      </div>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
